import { z } from 'zod';
import {
  AcceleratorBatch,
  type UserAcceleratorProfile,
  type UserExtendedProfile,
  type UserProfile,
  type editProfileSchema,
  usernameSchema,
} from '../lib';

export const listProfilesQuerySchema = z.object({
  nameQuery: z.string().optional(),
  languages: z.array(z.string()).optional(),
  batches: z.array(z.nativeEnum(AcceleratorBatch)).optional(),
  countries: z.array(z.string()).optional(),
  limit: z.coerce.number().optional(),
  offset: z.coerce.number().optional(),
});

export type ListProfilesQuery = z.infer<typeof listProfilesQuerySchema>;

export type ListProfilesResponse = {
  profiles: Array<UserProfile>;
  hasMore: boolean;
  offset: number;
};

export const retrieveUserIdentifierSchema = z.object({
  id: z.coerce.number().optional(),
  username: z.string().optional(),
});

export type RetrieveUserIdentifier = z.infer<
  typeof retrieveUserIdentifierSchema
>;

export const retrieveProfileQuerySchema =
  retrieveUserIdentifierSchema.superRefine((data, ctx) => {
    if (
      (data.id === undefined && data.username === undefined) ||
      (data.id !== undefined && data.username !== undefined)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Either 'id' or 'username' must be provided, but not both",
      });
    }
  });

export type RetrieveProfileQuery = z.input<typeof retrieveProfileQuerySchema>;

export const retrieveProfileCompletionQuerySchema = retrieveProfileQuerySchema;

export type RetrieveProfileCompletionQuery = z.input<
  typeof retrieveProfileCompletionQuerySchema
>;

export type RetrieveProfileCompletionResponse = ProfileCompletion;

export interface ProfileCompletionField {
  category: 'Location' | 'Personal' | 'Professional';
  name: string;
  completed: boolean;
}

export type ProfileCompletion = {
  fields: ProfileCompletionField[];
  completed: boolean;
};

export type RetrieveProfileResponse = {
  profile: UserProfile;
  extendedProfile: UserExtendedProfile;
  acceleratorProfile?: UserAcceleratorProfile;
};

export type EditProfileBody = z.infer<typeof editProfileSchema>;

export type EditProfileResponse = {
  user: {
    id: number;
    username: string;
  };
};

export const usernameValidationSchema = z.object({
  username: usernameSchema,
});

export type UsernameValidationQuery = z.infer<typeof usernameValidationSchema>;

export const UsernameValidationCode = {
  UNAVAILABLE: 'UNAVAILABLE',
  AVAILABLE: 'AVAILABLE',
} as const;

export type UsernameValidationCode =
  (typeof UsernameValidationCode)[keyof typeof UsernameValidationCode];

export type UsernameValidationResponse = {
  code: UsernameValidationCode;
  message: string;
};
